<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯標籤' : '新增標籤' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleCancel">取消</button>
        <button type="button" class="btn btn-primary" :disabled="submitting || isSystemTag" @click="handleSubmit">
          <span v-if="submitting">處理中...</span>
          <span v-else>{{ isEdit ? '更新' : '建立' }}</span>
        </button>
      </div>
    </div>

    <div class="page-container">
      <div v-if="isSystemTag" class="alert alert-warning">
        <strong>注意：</strong>此標籤為系統標籤，無法編輯或刪除。
      </div>
      <div v-if="isAutoRuleTag" class="alert alert-info">
        <strong>注意：</strong>此標籤為自動規則標籤，可編輯名稱、顏色、圖示與說明，不能設為限時標籤。修改名稱會同步到使用此名稱的自動貼標規則。此頁不能刪除。
      </div>
      <div v-if="isStoreTag" class="alert alert-info">
        <strong>注意：</strong>此標籤為門市標籤，可編輯顏色、圖示、說明等欄位，但標籤名稱由系統自動管理，無法修改。
      </div>
      <form @submit.prevent="handleSubmit">
        <div class="form-card">
          <div class="active-switch" :class="{ on: form.isActive, disabled: isSystemTag }">
            <div class="active-switch-copy">
              <span class="active-switch-title">啟用標籤</span>
              <span class="active-switch-status">
                {{ form.isActive ? '已啟用，可被貼上與使用' : '已停用，不會被使用' }}
              </span>
            </div>
            <label class="toggle-switch">
              <input
                v-model="form.isActive"
                type="checkbox"
                :disabled="isSystemTag"
                aria-label="啟用標籤"
              />
              <span class="toggle-slider"></span>
            </label>
          </div>

          <h3 class="section-title">基本資訊</h3>
          
          <div class="form-row">
            <div class="form-group">
              <label for="tagName" class="required">標籤名稱</label>
              <input
                id="tagName"
                v-model="form.tagName"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.tagName }"
                :disabled="isSystemTag || isStoreTag"
                placeholder="例如：VIP會員"
                :required="!isStoreTag"
                maxlength="100"
              />
              <p v-if="isStoreTag" class="form-hint">門市標籤名稱由系統自動管理，無法修改</p>
              <div v-if="errors.tagName" class="invalid-feedback">
                {{ errors.tagName }}
              </div>
            </div>

            <div class="form-group">
              <label for="tagColor">標籤顏色</label>
              <div
                class="color-swatch-grid"
                :class="{ disabled: isSystemTag }"
              >
                <button
                  v-for="swatch in tagColorSwatches"
                  :key="swatch.value"
                  type="button"
                  class="color-swatch"
                  :class="{ selected: isSwatchSelected(swatch.value) }"
                  :style="{ backgroundColor: swatch.value }"
                  :title="swatch.label"
                  :aria-label="swatch.label"
                  :disabled="isSystemTag"
                  @click="selectSwatch(swatch.value)"
                >
                  <i
                    v-if="isSwatchSelected(swatch.value)"
                    class="ri-check-line"
                    :class="{ 'is-dark': isLightColor(swatch.value) }"
                  ></i>
                </button>
              </div>
              <div class="color-custom-row">
                <span class="color-custom-label">自訂顏色</span>
                <div class="color-input-group">
                  <input
                    id="tagColor"
                    v-model="form.tagColor"
                    type="color"
                    class="form-control color-picker"
                    :class="{ 'is-invalid': errors.tagColor }"
                    :disabled="isSystemTag"
                  />
                  <input
                    v-model="form.tagColor"
                    type="text"
                    class="form-control color-text"
                    :disabled="isSystemTag"
                    placeholder="#4CAF50"
                    pattern="^#[0-9A-Fa-f]{6}$"
                    maxlength="7"
                  />
                </div>
              </div>
              <div v-if="errors.tagColor" class="invalid-feedback">
                {{ errors.tagColor }}
              </div>
              <p class="form-hint">點選色票快速選擇，或輸入 HEX 色碼（例如：#4CAF50）</p>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="tagIcon">圖示</label>
              <IconPicker
                :model-value="form.tagIcon"
                @update:model-value="form.tagIcon = $event"
                :disabled="isSystemTag"
              />
              <div v-if="errors.tagIcon" class="invalid-feedback">
                {{ errors.tagIcon }}
              </div>
              <p class="form-hint">點擊選擇 RemixIcon 圖示，或留空不使用圖示</p>
            </div>
          </div>

          <div class="form-group">
            <label for="description">說明</label>
            <textarea
              id="description"
              v-model="form.description"
              class="form-control"
              :class="{ 'is-invalid': errors.description }"
              :disabled="isSystemTag"
              rows="3"
              placeholder="標籤的說明文字"
            ></textarea>
            <div v-if="errors.description" class="invalid-feedback">
              {{ errors.description }}
            </div>
          </div>
        </div>

        <div v-if="disallowTemporary" class="form-card">
          <h3 class="section-title">限時標籤設定</h3>
          <p class="form-hint">{{ isAutoRuleTag ? '自動規則標籤不能設定為限時標籤。' : '專屬標籤不能設定為限時標籤。' }}</p>
        </div>
        <div v-else class="form-card">
          <h3 class="section-title">限時標籤設定（選填）</h3>
          
          <div class="form-group">
            <label class="checkbox-label">
              <input
                v-model="form.isTemporary"
                type="checkbox"
                class="form-checkbox"
                :disabled="isSystemTag"
              />
              <span>這是限時標籤</span>
            </label>
            <p class="form-hint">限時標籤會在指定時間後自動過期</p>
          </div>

          <div v-if="form.isTemporary" class="form-row">
            <div class="form-group">
              <label for="expiryRule">過期規則</label>
              <select
                id="expiryRule"
                v-model="form.expiryRule"
                class="form-control"
                :class="{ 'is-invalid': errors.expiryRule }"
                :disabled="isSystemTag"
              >
                <option value="">請選擇過期規則</option>
                <option value="days_from_assign">從貼標日起算天數</option>
                <option value="fixed_date">固定日期過期</option>
                <option value="end_of_month">月底過期</option>
                <option value="end_of_quarter">季末過期</option>
              </select>
              <div v-if="errors.expiryRule" class="invalid-feedback">
                {{ errors.expiryRule }}
              </div>

              <!-- 各規則說明 -->
              <div v-if="form.expiryRule === 'days_from_assign'" class="rule-hint">
                <i class="ri-information-line"></i>
                貼上標籤當天起算，滿指定天數後自動移除。<br>
                <span class="rule-example">範例：有效天數設為 30 天，1/1 貼標 → 1/31 過期</span>
              </div>
              <div v-else-if="form.expiryRule === 'fixed_date'" class="rule-hint">
                <i class="ri-information-line"></i>
                所有貼此標籤的會員，統一在指定日期當天過期。<br>
                <span class="rule-example">範例：設定過期日為 2025/12/31，無論何時貼標，均在該日移除</span>
              </div>
              <div v-else-if="form.expiryRule === 'end_of_month'" class="rule-hint">
                <i class="ri-information-line"></i>
                貼標後，標籤於當月最後一天的 23:59 自動過期。<br>
                <span class="rule-example">範例：3/15 貼標 → 3/31 過期；3/31 貼標 → 同日當天過期</span>
              </div>
              <div v-else-if="form.expiryRule === 'end_of_quarter'" class="rule-hint">
                <i class="ri-information-line"></i>
                貼標後，標籤於當季最後一天的 23:59 自動過期。<br>
                季度區間：Q1（1–3 月）、Q2（4–6 月）、Q3（7–9 月）、Q4（10–12 月）<br>
                <span class="rule-example">範例：2/10 貼標 → 3/31 過期；5/1 貼標 → 6/30 過期</span>
              </div>
            </div>

            <div v-if="form.expiryRule === 'days_from_assign'" class="form-group">
              <label for="defaultValidityDays">預設有效天數</label>
              <input
                id="defaultValidityDays"
                v-model.number="form.defaultValidityDays"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.defaultValidityDays }"
                :disabled="isSystemTag"
                placeholder="例如：30"
                min="1"
              />
              <div v-if="errors.defaultValidityDays" class="invalid-feedback">
                {{ errors.defaultValidityDays }}
              </div>
            </div>

            <div v-if="form.expiryRule === 'fixed_date'" class="form-group">
              <label for="defaultExpiryDate">預設過期日期</label>
              <input
                id="defaultExpiryDate"
                v-model="form.defaultExpiryDate"
                type="date"
                class="form-control"
                :class="{ 'is-invalid': errors.defaultExpiryDate }"
                :disabled="isSystemTag"
              />
              <div v-if="errors.defaultExpiryDate" class="invalid-feedback">
                {{ errors.defaultExpiryDate }}
              </div>
            </div>
          </div>
        </div>

        <div v-if="submitError" class="alert alert-danger">
          {{ submitError }}
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { tagService } from '@/api/modules/tags.js';
import { showError, showSuccess } from '@/utils/swal.js';
import IconPicker from '@/components/IconPicker.vue';

const TAG_COLOR_SWATCHES = [
  { label: '紅色', value: '#EF4444' },
  { label: '橘色', value: '#F97316' },
  { label: '琥珀色', value: '#F59E0B' },
  { label: '黃色', value: '#EAB308' },
  { label: '萊姆綠', value: '#84CC16' },
  { label: '綠色', value: '#4CAF50' },
  { label: '翠綠', value: '#10B981' },
  { label: '青色', value: '#14B8A6' },
  { label: '天藍', value: '#0EA5E9' },
  { label: '藍色', value: '#3B82F6' },
  { label: '靛藍', value: '#6366F1' },
  { label: '紫色', value: '#8B5CF6' },
  { label: '粉紫', value: '#A855F7' },
  { label: '粉色', value: '#EC4899' },
  { label: '玫瑰色', value: '#F43F5E' },
  { label: '灰色', value: '#6B7280' },
  { label: '深灰', value: '#374151' },
  { label: '棕色', value: '#92400E' },
];

export default {
  name: 'ClientTagCreate',
  components: {
    IconPicker
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const tagId = computed(() => route.params.id);
    const isEdit = computed(() => !!tagId.value);
    const { pageTitle } = usePageTitle(isEdit.value ? '編輯標籤' : '新增標籤');

    const form = ref({
      tagName: '',
      tagColor: '#4CAF50',
      tagIcon: '',
      description: '',
      isActive: true,
      isTemporary: false,
      expiryRule: '',
      defaultValidityDays: null,
      defaultExpiryDate: ''
    });

    const tagSource = ref(null); // 標籤來源（system, auto_rule, exclusive, manual=互動標籤）
    const tagName = ref(''); // 原始標籤名稱（用於判斷是否為門市標籤）
    const isSystemTag = computed(() => tagSource.value === 'system');
    const isAutoRuleTag = computed(() => tagSource.value === 'auto_rule');
    const isExclusiveTag = computed(() => tagSource.value === 'exclusive');
    const disallowTemporary = computed(() => isAutoRuleTag.value || isExclusiveTag.value);
    const isStoreTag = computed(() => {
      return isExclusiveTag.value && tagName.value && tagName.value.startsWith('store_');
    });

    const errors = ref({});
    const submitError = ref('');
    const submitting = ref(false);
    const loading = ref(false);

    const loadTag = async () => {
      if (!isEdit.value) return;

      loading.value = true;
      try {
        const response = await tagService.getTag(tagId.value);

        if (response && response.success && response.data) {
          const tag = response.data;
          tagSource.value = tag.tagSource || 'manual';
          tagName.value = tag.tagName || '';
          form.value = {
            tagName: tag.tagName || '',
            tagColor: tag.tagColor || '#4CAF50',
            tagIcon: tag.tagIcon || '',
            description: tag.description || '',
            isActive: tag.isActive !== undefined ? tag.isActive : true,
            isTemporary: tag.tagSource === 'exclusive' || tag.tagSource === 'auto_rule' ? false : (tag.isTemporary || false),
            expiryRule: tag.expiryRule || '',
            defaultValidityDays: tag.defaultValidityDays || null,
            defaultExpiryDate: tag.defaultExpiryDate || ''
          };
        } else {
          await showError('載入失敗', response?.message || '獲取標籤資料失敗');
          router.push('/client/tags');
        }
      } catch (err) {
        console.error('獲取標籤資料失敗:', err);
        await showError('載入失敗', err.message || '獲取標籤資料失敗');
        router.push('/client/tags');
      } finally {
        loading.value = false;
      }
    };

    const validateForm = () => {
      errors.value = {};

      // 門市標籤不需要驗證 tagName（因為不能修改）
      if (!isStoreTag.value && (!form.value.tagName || !form.value.tagName.trim())) {
        errors.value.tagName = '標籤名稱不能為空';
      }

      if (form.value.tagColor && !/^#[0-9A-Fa-f]{6}$/.test(form.value.tagColor)) {
        errors.value.tagColor = '標籤顏色格式錯誤，應為 HEX 色碼（如：#4CAF50）';
      }

      if (!disallowTemporary.value && form.value.isTemporary) {
        if (!form.value.expiryRule) {
          errors.value.expiryRule = '限時標籤必須選擇過期規則';
        } else if (form.value.expiryRule === 'days_from_assign' && !form.value.defaultValidityDays) {
          errors.value.defaultValidityDays = '請輸入預設有效天數';
        } else if (form.value.expiryRule === 'fixed_date' && !form.value.defaultExpiryDate) {
          errors.value.defaultExpiryDate = '請選擇預設過期日期';
        }
      }

      return Object.keys(errors.value).length === 0;
    };

    const handleSubmit = async () => {
      if (!validateForm()) {
        submitError.value = '請修正表單錯誤';
        return;
      }

      submitting.value = true;
      submitError.value = '';

      try {
        const tagData = {
          isActive: form.value.isActive,
          isTemporary: disallowTemporary.value ? false : form.value.isTemporary
        };

        // 門市標籤不提交 tagName（由系統管理）
        if (!isStoreTag.value) {
          tagData.tagName = form.value.tagName.trim();
        }

        // 可選欄位：只在有值時才加入
        if (form.value.tagColor && form.value.tagColor.trim()) {
          tagData.tagColor = form.value.tagColor.trim();
        }
        if (form.value.tagIcon && form.value.tagIcon.trim()) {
          tagData.tagIcon = form.value.tagIcon.trim();
        }
        if (form.value.description && form.value.description.trim()) {
          tagData.description = form.value.description.trim();
        }

        // 限時標籤相關欄位處理
        if (form.value.isTemporary === true) {
          // 是限時標籤：只在有 expiryRule 時才加入
          if (form.value.expiryRule && form.value.expiryRule.trim()) {
            tagData.expiryRule = form.value.expiryRule.trim();
            
            if (form.value.expiryRule === 'days_from_assign' && form.value.defaultValidityDays && form.value.defaultValidityDays >= 1) {
              tagData.defaultValidityDays = form.value.defaultValidityDays;
            }
            
            if (form.value.expiryRule === 'fixed_date' && form.value.defaultExpiryDate && form.value.defaultExpiryDate.trim()) {
              tagData.defaultExpiryDate = form.value.defaultExpiryDate.trim();
            }
          }
        } else {
          // 不是限時標籤：明確清除這些欄位（設為 null）
          // 這樣可以確保如果原本是限時標籤，改為非限時標籤時，資料庫中的值會被清除
          tagData.expiryRule = null;
          tagData.defaultValidityDays = null;
          tagData.defaultExpiryDate = null;
        }

        let response;
        if (isEdit.value) {
          response = await tagService.updateTag(tagId.value, tagData);
        } else {
          response = await tagService.createTag(tagData);
        }

        if (response && response.success) {
          await showSuccess(isEdit.value ? '更新成功' : '建立成功', '標籤已儲存');
          router.push('/client/tags');
        } else {
          submitError.value = response?.message || (isEdit.value ? '更新失敗' : '建立失敗');
        }
      } catch (err) {
        console.error('儲存標籤失敗:', err);
        const errorMessage = err.response?.data?.message || err.message || (isEdit.value ? '更新失敗' : '建立失敗');
        submitError.value = errorMessage;
        await showError('儲存失敗', errorMessage);
      } finally {
        submitting.value = false;
      }
    };

    const handleCancel = () => {
      router.push('/client/tags');
    };

    const normalizeHex = (value) => (value || '').trim().toUpperCase();

    const isSwatchSelected = (color) => normalizeHex(form.value.tagColor) === normalizeHex(color);

    const selectSwatch = (color) => {
      if (isSystemTag.value) return;
      form.value.tagColor = color;
      if (errors.value.tagColor) delete errors.value.tagColor;
    };

    const isLightColor = (hex) => {
      const raw = normalizeHex(hex).replace('#', '');
      if (raw.length !== 6) return false;
      const r = parseInt(raw.slice(0, 2), 16);
      const g = parseInt(raw.slice(2, 4), 16);
      const b = parseInt(raw.slice(4, 6), 16);
      return (r * 299 + g * 587 + b * 114) / 1000 > 160;
    };

    // 監聽 isTemporary 變化，當取消勾選時清除相關欄位
    watch(() => form.value.isTemporary, (newValue) => {
      if (!newValue) {
        // 取消勾選限時標籤時，清除所有相關欄位
        form.value.expiryRule = '';
        form.value.defaultValidityDays = null;
        form.value.defaultExpiryDate = '';
        // 清除相關錯誤訊息
        if (errors.value.expiryRule) delete errors.value.expiryRule;
        if (errors.value.defaultValidityDays) delete errors.value.defaultValidityDays;
        if (errors.value.defaultExpiryDate) delete errors.value.defaultExpiryDate;
      }
    });

    onMounted(() => {
      loadTag();
    });

    return {
      isEdit,
      pageTitle,
      form,
      tagSource,
      isSystemTag,
      isAutoRuleTag,
      disallowTemporary,
      isExclusiveTag,
      isStoreTag,
      errors,
      submitError,
      submitting,
      loading,
      tagColorSwatches: TAG_COLOR_SWATCHES,
      isSwatchSelected,
      selectSwatch,
      isLightColor,
      handleSubmit,
      handleCancel,
    };
  },
};
</script>

<style scoped>
.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.header-left { display: flex; align-items: center; }
.header-actions { display: flex; align-items: center; gap: 8px; }

.page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.page-container {
  padding: 20px;
  padding-top: 72px;
}

.form-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 24px;
  margin-bottom: 16px;
}

.form-card:last-of-type { margin-bottom: 0; }

.active-switch {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: -24px -24px 20px;
  padding: 16px 24px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  border-radius: 10px 10px 0 0;
}

.active-switch.on {
  background: #f0fdf4;
  border-bottom-color: #bbf7d0;
}

.active-switch.disabled {
  opacity: 0.65;
}

.active-switch-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.active-switch-title {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
}

.active-switch-status {
  font-size: 13px;
  color: #6b7280;
}

.active-switch.on .active-switch-status {
  color: #15803d;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 56px;
  height: 32px;
  flex-shrink: 0;
  margin: 0;
  cursor: pointer;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  inset: 0;
  background: #d1d5db;
  border-radius: 32px;
  transition: background 0.2s;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  width: 24px;
  height: 24px;
  left: 4px;
  top: 4px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s;
}

.toggle-switch input:checked + .toggle-slider {
  background: #16a34a;
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(24px);
}

.toggle-switch input:disabled + .toggle-slider {
  cursor: not-allowed;
}

.toggle-switch input:focus-visible + .toggle-slider {
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.25);
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
  color: #111827;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 20px;
}

label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-control.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
}

.form-hint {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
  margin-bottom: 0;
}

.rule-hint {
  margin-top: 8px;
  padding: 10px 12px;
  background: #f0f7ff;
  border-left: 3px solid #4a90d9;
  border-radius: 4px;
  font-size: 13px;
  color: #555;
  line-height: 1.7;
}

.rule-hint i {
  color: #4a90d9;
  margin-right: 4px;
}

.rule-example {
  display: inline-block;
  margin-top: 4px;
  font-size: 12px;
  color: #888;
  font-style: italic;
}

.color-swatch-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.color-swatch-grid.disabled {
  opacity: 0.55;
  pointer-events: none;
}

.color-swatch {
  width: 28px;
  height: 28px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.12);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}

.color-swatch:hover:not(:disabled) {
  transform: scale(1.08);
}

.color-swatch.selected {
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px #3b82f6, inset 0 0 0 1px rgba(0, 0, 0, 0.12);
}

.color-swatch i {
  font-size: 14px;
  color: #fff;
  line-height: 1;
}

.color-swatch i.is-dark {
  color: #1f2937;
}

.color-custom-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.color-custom-label {
  flex-shrink: 0;
  font-size: 13px;
  color: #6b7280;
}

.color-input-group {
  display: flex;
  gap: 8px;
  flex: 1;
}

.color-picker {
  width: 60px;
  padding: 4px;
  cursor: pointer;
}

.color-text {
  flex: 1;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.form-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.btn { padding: 6px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; border: none; display: inline-flex; align-items: center; gap: 6px; }
.btn-primary { background: #3b82f6; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb; }
.btn-secondary:hover:not(:disabled) { background: #e5e7eb; }

.alert {
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 16px;
}

.alert-danger {
  background-color: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  margin-top: 16px;
  margin-bottom: 0;
}

.alert-warning {
  background-color: #fffbeb;
  color: #92400e;
  border: 1px solid #fde68a;
}

.alert-info {
  background-color: #eff6ff;
  color: #1e40af;
  border: 1px solid #bfdbfe;
}

.form-control:disabled,
.form-checkbox:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
